<template>
  <div class="ar-wrap">

    <!-- Loading -->
    <div class="state-box" v-if="loadingData">
      <i class="fa fa-spinner fa-spin"></i> Loading…
    </div>

    <!-- Empty -->
    <div class="state-box" v-else-if="!loadingData && archival_datas.length === 0">
      <i class="fa fa-archive"></i> No archival periods found
    </div>

    <template v-else>
      <!-- Toolbar -->
      <div class="toolbar">
        <label class="select-all-wrap">
          <input type="checkbox" v-model="selectAll" @change="selectAllArchivals()" />
          <span>Select all</span>
        </label>
        <div class="toolbar-stats">
          <span class="stat-chip blue">{{ archival_datas.length }} total</span>
          <span class="stat-chip green">{{ selectedCount }} selected</span>
        </div>
      </div>

      <!-- List header -->
      <div class="list-header">
        <div></div>
        <div>Archival Period</div>
        <div style="text-align:right;">Def. Rate</div>
        <div style="text-align:right;">Def. Add. Rate</div>
        <div style="text-align:right;">Def. Tax</div>
        <div style="text-align:right;">Def. Commission</div>
        <div style="text-align:center;">Status</div>
        <div></div>
      </div>

      <!-- Archival rows -->
      <div
        v-for="(data, index) in archival_datas"
        :key="index"
        class="ar-row"
        :class="{ 'is-expanded': !!expandedRows[index], 'is-selected': data.checked }"
      >
        <!-- Collapsed row head -->
        <div class="ar-row-head" @click="toggleExpand(index)">
          <div class="arh-check" @click.stop>
            <input type="checkbox" v-model="data.checked" />
          </div>
          <div class="arh-name">
            <div class="ar-icon"><i class="fa fa-archive"></i></div>
            <span class="ar-label">{{ data.archival }}</span>
          </div>
          <div class="arh-val">{{ data.default_rate ? ('$' + data.default_rate) : '—' }}</div>
          <div class="arh-val">{{ data.default_additional_rate ? ('$' + data.default_additional_rate) : '—' }}</div>
          <div class="arh-val">{{ data.default_tax ? (data.default_tax + '%') : '—' }}</div>
          <div class="arh-val">{{ data.default_commission ? ('$' + data.default_commission) : '—' }}</div>
          <div style="text-align:center;">
            <span v-if="hasCustom(data)" class="status-badge s-custom">
              <i class="fa fa-check-circle"></i> Custom
            </span>
            <span v-else class="status-badge s-default">Default</span>
          </div>
          <div class="arh-toggle">
            <i class="fa fa-chevron-down expand-icon" :class="{ rotated: expandedRows[index] }"></i>
          </div>
        </div>

        <!-- Expanded comparison panel -->
        <div class="ar-detail" v-if="expandedRows[index]">
          <div class="rate-compare">
            <div class="rc-header">
              <div class="rc-col">Field</div>
              <div><span class="rc-badge def"><i class="fa fa-lock"></i> Default</span></div>
              <div><span class="rc-badge cus"><i class="fa fa-edit"></i> Custom</span></div>
            </div>

            <div class="rc-row">
              <div class="rc-field">Rate</div>
              <div><span class="rc-def-val">${{ data.default_rate || '—' }}</span></div>
              <div>
                <div class="input-wrap">
                  <span class="ip">$</span>
                  <input class="rate-input" type="number" min="0" v-model="data.rate" placeholder="0.00" />
                </div>
              </div>
            </div>

            <div class="rc-row" v-if="data.has_additional_rate">
              <div class="rc-field">Add. Rate</div>
              <div><span class="rc-def-val">${{ data.default_additional_rate || '—' }}</span></div>
              <div>
                <div class="input-wrap">
                  <span class="ip">$</span>
                  <input class="rate-input" type="text" v-model="data.additional_rate" placeholder="0.00" />
                </div>
              </div>
            </div>

            <div class="rc-row">
              <div class="rc-field">Tax</div>
              <div><span class="rc-def-val">{{ data.default_tax || '—' }}%</span></div>
              <div>
                <div class="input-wrap">
                  <span class="ip">%</span>
                  <input class="rate-input" type="number" min="0" v-model="data.tax_percentage" placeholder="0" />
                </div>
              </div>
            </div>

            <div class="rc-row">
              <div class="rc-field">Commission</div>
              <div><span class="rc-def-val">${{ data.default_commission || '—' }}</span></div>
              <div>
                <div class="input-wrap">
                  <span class="ip">$</span>
                  <input class="rate-input" type="number" min="0" v-model="data.commission_percentage" placeholder="0.00" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Save bar -->
    <div class="save-bar" v-if="!loadingData && archival_datas.length > 0">
      <div class="save-bar__inner">
        <span class="save-bar__info">
          {{ client.name ? client.name + ' — Archival Rates' : 'Archival Rates' }}
          <span class="save-bar__count" v-if="selectedCount > 0">({{ selectedCount }} selected)</span>
        </span>
        <button class="btn-save" ref="kt_save_archival_rate" @click="update('S', 'kt_save_archival_rate')">
          <i class="fa fa-check"></i> Save Rates
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import {
  FETCH_B_ARCHIVAL,
  FETCH_B_ARCHIVAL_RATE_LISTS,
  UPDATE_B_ARCHIVAL_RATE_LISTS
} from "@/core/services/store/actions.type";
import { mapGetters } from "vuex";

export default {
  data() {
    return {
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Locations", route: "client.location" },
        { id: 3, title: "Update Rate", route: "" }
      ],
      archival_datas: [],
      errors: [],
      loadingData: false,
      serviceError: "",
      selectAll: false,
      archivals: [],
      archival_rates: [],
      client: [],
      expandedRows: {}
    };
  },
  computed: {
    ...mapGetters(["getBArchivals", "getBArchivalRateLists"]),
    selectedCount() {
      return this.archival_datas.filter(d => d.checked).length;
    }
  },
  created() {
    this.breadCrumbs[2].title = "Edit Rate";
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.getArchivalData();
  },
  methods: {
    toggleExpand(index) {
      this.$set(this.expandedRows, index, !this.expandedRows[index]);
    },
    hasCustom(data) {
      return data.rate || data.tax_percentage || data.commission_percentage;
    },
    selectAllArchivals() {
      this.archival_datas.forEach(a => { a.checked = this.selectAll; });
    },
    closeBtnLoad(ref) {
      const b = this.$refs[ref];
      if (b) { b.classList.remove("spinner","spinner-light","spinner-right"); b.disabled = false; }
    },
    loadingButton(ref) {
      const b = this.$refs[ref];
      if (b) { b.disabled = true; b.classList.add("spinner","spinner-light","spinner-right"); }
    },
    getArchivalData() {
      this.loadingData = true;
      this.$store.dispatch(FETCH_B_ARCHIVAL).then(data_archival => {
        this.$store.dispatch(FETCH_B_ARCHIVAL_RATE_LISTS, this.$route.params.id)
          .then(data => {
            this.archival_rates = data.archival_rates;
            this.client = data.client;
            data_archival.forEach(archival => {
              const key = this.archival_rates.findIndex(x => x.archival_period.id === archival.id);
              if (key === -1) {
                this.archival_datas.push({
                  id: archival.id, archival: archival.name,
                  rate: "", has_additional_rate: archival.has_additional_rate,
                  additional_rate: "", tax_percentage: "", commission_percentage: "",
                  checked: false,
                  default_rate: archival.default_rate,
                  default_additional_rate: archival.additional_rate,
                  default_tax: archival.tax_percentage,
                  default_commission: archival.commission_percentage
                });
              } else {
                const r = this.archival_rates[key];
                this.archival_datas.push({
                  id: archival.id, archival: archival.name,
                  rate: r.rate, has_additional_rate: r.has_additional_rate,
                  additional_rate: r.additional_rate, tax_percentage: r.tax_percentage,
                  commission_percentage: r.commission_percentage, checked: true,
                  default_rate: archival.default_rate,
                  default_additional_rate: archival.additional_rate,
                  default_tax: archival.tax_percentage,
                  default_commission: archival.commission_percentage
                });
              }
            });
            this.loadingData = false;
          })
          .catch(() => { this.loadingData = false; });
      });
    },
    update(saveType, ref) {
      const archival_datas = this.archival_datas.filter(a => a.checked);
      if (archival_datas.length === 0) {
        this.$toastr.e("Please select at least one archival period");
        return;
      }
      this.errors = [];
      this.loadingButton(ref);
      this.$store.dispatch(UPDATE_B_ARCHIVAL_RATE_LISTS, {
        location_id: this.$route.params.id,
        rate_list: archival_datas
      })
        .then(data => {
          this.$toastr.s(data.msg);
          this.closeBtnLoad(ref);
          if (saveType === "S") {
            this.$router.push({ name: "client.location" });
          }
        })
        .catch(err => {
          this.errors = err;
          this.selectAll = false;
          this.serviceError = "";
          this.archival_datas = [];
          this.getArchivalData();
          this.closeBtnLoad(ref);
        });
    }
  }
};
</script>

<style lang="scss" scoped>
$font:   'Segoe UI', sans-serif;
$blue:   #389ad4;
$dark:   #0d1b3e;
$green:  #276749;
$border: #dde2ec;

/* ── Toolbar ── */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  border-bottom: 1px solid #f0f2f8;
  background: #fafbfd;

  .select-all-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    color: #4a5568;
    cursor: pointer;

    input[type=checkbox] { cursor: pointer; }
  }

  .toolbar-stats {
    display: flex;
    gap: 8px;

    .stat-chip {
      padding: 3px 10px;
      border-radius: 20px;
      font-size: 11px;
      font-weight: 700;

      &.blue  { background: #dbeafe; color: #1d4ed8; }
      &.green { background: #dcfce7; color: $green; }
    }
  }
}

/* ── List header ── */
.list-header {
  display: grid;
  grid-template-columns: 32px 1fr 110px 110px 90px 110px 100px 36px;
  align-items: center;
  padding: 8px 20px;
  background: #fafbfd;
  border-bottom: 2px solid #eef0f8;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #a0aab8;
  gap: 8px;
}

/* ── Archival row ── */
.ar-row {
  border-bottom: 1px solid #f5f7fa;

  &:last-child { border-bottom: none; }

  &.is-expanded > .ar-row-head {
    background: #f8fbff;
    border-bottom: 1px solid #e8edf5;
  }

  &.is-selected > .ar-row-head {
    background: #f0f9f4;
  }
}

.ar-row-head {
  display: grid;
  grid-template-columns: 32px 1fr 110px 110px 90px 110px 100px 36px;
  align-items: center;
  padding: 10px 20px;
  cursor: pointer;
  gap: 8px;
  transition: background 0.12s;

  &:hover { background: #f8fbff; }
}

.arh-check {
  display: flex;
  align-items: center;
  justify-content: center;

  input[type=checkbox] { cursor: pointer; }
}

.arh-name {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;

  .ar-icon {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    background: #fef3c7;
    color: #92400e;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    flex-shrink: 0;
  }

  .ar-label {
    font-size: 13px;
    font-weight: 400;
    color: $dark;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.arh-val {
  text-align: right;
  font-size: 12px;
  font-family: monospace;
  font-weight: 600;
  color: #7a8499;
}

.arh-toggle {
  display: flex;
  justify-content: center;

  .expand-icon {
    font-size: 11px;
    color: #a0aab8;
    transition: transform 0.2s;
    &.rotated { transform: rotate(180deg); }
  }
}

/* ── Status badges ── */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;

  &.s-custom  { background: #dcfce7; color: $green; }
  &.s-default { background: #e2e8f0; color: #718096; }
}

/* ── Expanded panel ── */
.ar-detail {
  background: #f9fbfd;
  border-top: 1px solid #eef0f8;
  padding: 14px 20px 14px 60px;
}

.rate-compare {
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 10px;
  overflow: hidden;
  max-width: 560px;
}

.rc-header {
  display: grid;
  grid-template-columns: 1fr 140px 180px;
  padding: 8px 16px;
  background: #f5f8fc;
  border-bottom: 1px solid #e8edf5;
  gap: 12px;

  .rc-col {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #a0aab8;
  }

  .rc-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 9px;
    border-radius: 20px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;

    &.def { background: #dbeafe; color: #1d4ed8; }
    &.cus { background: #dcfce7; color: $green; }
  }
}

.rc-row {
  display: grid;
  grid-template-columns: 1fr 140px 180px;
  align-items: center;
  padding: 9px 16px;
  border-bottom: 1px solid #f5f7fa;
  gap: 12px;

  &:last-child { border-bottom: none; }
  &:hover { background: #fafcff; }

  .rc-field {
    font-size: 12px;
    font-weight: 600;
    color: #4a5568;
  }

  .rc-def-val {
    font-size: 12px;
    font-family: monospace;
    font-weight: 600;
    color: #7a8499;
    padding: 4px 10px;
    background: #f4f8fe;
    border-radius: 5px;
    display: inline-block;
  }
}

/* ── Input ── */
.input-wrap {
  display: flex;
  align-items: center;
  height: 30px;
  border: 1.5px solid $border;
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
  transition: border-color 0.15s;

  &:focus-within { border-color: $blue; box-shadow: 0 0 0 2px rgba(56,154,212,0.1); }

  .ip {
    height: 100%;
    padding: 0 6px 0 8px;
    background: #f5f7fa;
    border-right: 1px solid #e2e8f0;
    color: #a0aab8;
    font-size: 11px;
    font-weight: 600;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    user-select: none;
  }

  .rate-input {
    flex: 1;
    border: none;
    outline: none;
    height: 100%;
    padding: 0 8px;
    font-size: 12.5px;
    color: $dark;
    font-family: $font;
    background: transparent;
    text-align: right;
    min-width: 0;

    &[type=number] { -moz-appearance: textfield; appearance: textfield; }
    &::-webkit-outer-spin-button, &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  }
}

/* ── States ── */
.state-box {
  text-align: center;
  padding: 56px 24px;
  color: #a0aab8;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

/* ── Save bar ── */
.save-bar {
  position: fixed;
  bottom: 2.5rem;
  width: calc(78vw);
  z-index: 200;

  .save-bar__inner {
    background: $dark;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(13,27,62,0.3);
    padding: 14px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .save-bar__info {
      font-size: 13px;
      color: rgba(255,255,255,0.75);
      font-family: $font;
    }

    .save-bar__count {
      font-size: 12px;
      color: rgba(255,255,255,0.5);
      margin-left: 6px;
    }

    .btn-save {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      height: 36px;
      padding: 0 22px;
      background: $green;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      font-family: $font;
      cursor: pointer;
      transition: opacity 0.2s;

      &:hover { opacity: 0.9; }
      &:disabled { opacity: 0.55; cursor: not-allowed; }
    }
  }
}
</style>
